import React, { useState } from 'react';
import { Dialog } from '@material-tailwind/react';
import {
    FileText, Download, Clock, CheckCircle, Loader2, X, Receipt, Wallet,
} from 'lucide-react';
import moment from 'moment';
import { useGetInvoices, useCustomerMarkInvoicePaid } from '../../custom-hooks/use-invoices';
import { INVOICE_STATUSES, PAYMENT_METHODS } from '../../utils/enums';
import { formatNumberWithCommas, Notify, convertToPresentableText } from '../../utils/functions';
import { downloadInvoicePdf } from '../../utils/invoice-pdf';
import { GetInvoiceById, GetInvoiceSettings } from '../../services/backend';
import ErrorX from '../../components/error-x';

const STATUS_BADGE = {
    draft: 'bg-cream-100 text-[#6B675F]',
    delivered: 'bg-blue-100 text-blue-700',
    partially_paid: 'bg-amber-100 text-amber-700',
    paid: 'bg-green-100 text-green-700',
    void: 'bg-red-100 text-red-700',
};
const statusLabels = {
    draft: 'Draft', delivered: 'Unpaid', partially_paid: 'Partially Paid', paid: 'Paid', void: 'Void',
};

const CustomerInvoicePage = () => {
    // No pagination in the customer portal — show every invoice this customer has. 200 is the
    // backend's hard cap (invoiceController.js), effectively "all of them" for a single customer.
    const { data, isLoading, isError, refetch } = useGetInvoices({ limit: 200 });
    const markPaidMutation = useCustomerMarkInvoicePaid();

    const [markPaidDialog, setMarkPaidDialog] = useState(null); // holds invoiceId
    const [markPaidForm, setMarkPaidForm] = useState({ paymentMethod: 'bank_transfer', note: '', amount: 0 });
    const [detailInvoice, setDetailInvoice] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const invoices = data?.invoices || [];

    const handleDownloadPdf = async (invoiceId) => {
        try {
            const [res, settingsRes] = await Promise.all([
                GetInvoiceById(invoiceId),
                GetInvoiceSettings().catch(() => null),
            ]);
            const invoice = res.data?.invoice;
            if (invoice) {
                downloadInvoicePdf(invoice, settingsRes?.data?.settings || {});
            }
        } catch (error) {
            Notify('Failed to download PDF', 'error');
        }
    };

    const handleViewDetail = async (invoiceId) => {
        try {
            setLoadingDetail(true);
            const res = await GetInvoiceById(invoiceId);
            setDetailInvoice(res.data?.invoice);
        } catch (error) {
            Notify('Failed to load invoice details', 'error');
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleMarkAsPaid = async () => {
        try {
            const inv = invoices.find(i => i._id === markPaidDialog);
            await markPaidMutation.mutateAsync({
                invoiceId: markPaidDialog,
                data: {
                    amount: parseFloat(markPaidForm.amount) || inv?.balanceAmount || 0,
                    paymentMethod: markPaidForm.paymentMethod,
                    note: markPaidForm.note,
                },
            });
            Notify('Payment marked! Pending admin approval.', 'success');
            setMarkPaidDialog(null);
            setMarkPaidForm({ paymentMethod: 'bank_transfer', note: '', amount: 0 });
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to mark as paid', 'error');
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-[60vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
        );
    }

    if (isError) {
        return <ErrorX title="Error loading invoices" message="Please try again later." onRetry={refetch} />;
    }

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header */}
            <div className="mb-6">
                <h1 className="font-display text-[25px] font-bold text-ink">My Invoices</h1>
                <p className="mt-1 text-sm text-[#8C8175]">View your billing and record payments.</p>
            </div>

            {invoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-[#ECE3D5] bg-white py-16 text-center">
                    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cream-input text-brand">
                        <Receipt className="h-7 w-7" />
                    </div>
                    <h3 className="font-display text-lg font-bold text-ink">No invoices yet</h3>
                    <p className="mx-auto mt-1 max-w-md text-sm text-[#8C8175]">
                        Your invoices will appear here once they are generated by the management.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {invoices.map((inv) => {
                        const hasPendingClaim = inv.payments?.some(p => p.markedByCustomer && !p.approvedByAdmin);

                        return (
                            <div
                                key={inv._id}
                                className="rounded-2xl border border-[#ECE3D5] bg-white p-6 transition-all hover:border-[#E0C3A4] hover:shadow-[0_14px_30px_-20px_rgba(40,22,8,0.4)]"
                            >
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                    {/* Invoice info */}
                                    <div className="flex items-start gap-3.5">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h3 className="font-display text-base font-semibold text-ink">{inv.invoiceNumber}</h3>
                                                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_BADGE[inv.status] || 'bg-cream-100 text-[#6B675F]'}`}>
                                                    {statusLabels[inv.status] || inv.status}
                                                </span>
                                                {hasPendingClaim && (
                                                    <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                                        <Clock className="h-3 w-3" /> Payment Pending Approval
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#8C8175]">
                                                <span>Period: {moment({ month: inv.billingPeriod?.month - 1, year: inv.billingPeriod?.year }).format('MMMM YYYY')}</span>
                                                {inv.dueDate && <span>Due: {moment(inv.dueDate).format('MMM DD, YYYY')}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Amounts */}
                                    <div className="flex gap-6 sm:gap-8">
                                        <div className="text-right">
                                            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A89C8C]">Total</div>
                                            <div className="mt-0.5 font-display font-bold text-ink">PKR {formatNumberWithCommas(inv.totalAmount)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A89C8C]">Paid</div>
                                            <div className="mt-0.5 font-display font-bold text-green-600">PKR {formatNumberWithCommas(inv.paidAmount)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A89C8C]">Due</div>
                                            <div className="mt-0.5 font-display font-bold text-red-600">PKR {formatNumberWithCommas(inv.balanceAmount)}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="mt-5 flex flex-wrap gap-2.5 border-t border-[#F1E9DC] pt-4">
                                    <button
                                        onClick={() => handleViewDetail(inv._id)}
                                        className="flex items-center gap-2 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-4 py-2 text-sm font-semibold text-[#3B2A1E] transition-colors hover:bg-cream"
                                    >
                                        <FileText className="h-4 w-4" /> Details
                                    </button>
                                    <button
                                        onClick={() => handleDownloadPdf(inv._id)}
                                        className="flex items-center gap-2 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-4 py-2 text-sm font-semibold text-[#3B2A1E] transition-colors hover:bg-cream"
                                    >
                                        <Download className="h-4 w-4" /> Download PDF
                                    </button>
                                    {inv.balanceAmount > 0 && !hasPendingClaim && inv.status !== INVOICE_STATUSES.VOID && (
                                        <button
                                            onClick={() => {
                                                setMarkPaidDialog(inv._id);
                                                setMarkPaidForm(prev => ({ ...prev, amount: inv.balanceAmount }));
                                            }}
                                            className="flex items-center gap-2 rounded-[11px] bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                                        >
                                            <CheckCircle className="h-4 w-4" /> Mark as Paid
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Invoice Detail Dialog */}
            <Dialog open={!!detailInvoice} handler={() => setDetailInvoice(null)} size="lg" className="rounded-2xl">
                <div className="flex items-center justify-between border-b border-[#ECE3D5] px-6 py-4">
                    <h4 className="font-display text-lg font-semibold text-ink">Invoice Details</h4>
                    <button onClick={() => setDetailInvoice(null)} className="flex h-9 w-9 items-center justify-center rounded-full text-[#8C8175] transition-colors hover:bg-cream-input hover:text-ink">
                        <X size={20} />
                    </button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                    {loadingDetail ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
                    ) : detailInvoice && (
                        <div className="space-y-5">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-display text-lg font-semibold text-ink">{detailInvoice.invoiceNumber}</div>
                                    <div className="text-sm text-[#8C8175]">
                                        {moment({ month: detailInvoice.billingPeriod?.month - 1, year: detailInvoice.billingPeriod?.year }).format('MMMM YYYY')}
                                    </div>
                                </div>
                                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_BADGE[detailInvoice.status] || 'bg-cream-100 text-[#6B675F]'}`}>
                                    {statusLabels[detailInvoice.status]}
                                </span>
                            </div>

                            {/* Line Items — grouped and number-free for customer view */}
                            <div>
                                <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#8C8175]">Items</div>
                                {(() => {
                                    const SECURITY_TYPE = 'security_deposit';
                                    const ADVANCE_TYPE = 'advance_deduction';

                                    const getCleanDescription = (item) => {
                                        const booking = item.booking;
                                        if (!booking) return item.description || 'Monthly Rent';
                                        if (booking.office) return 'Office - Monthly Rent';
                                        if (booking.seat) {
                                            const seatType = booking.seat?.seatType;
                                            return seatType === 'dedicated' ? 'Dedicated Seat - Monthly Rent' : 'Shared Seat - Monthly Rent';
                                        }
                                        return item.description || 'Monthly Rent';
                                    };

                                    const rentItems = [];
                                    const securityItems = [];
                                    const advanceItems = [];

                                    for (const item of detailInvoice.lineItems || []) {
                                        if (item.type === SECURITY_TYPE) securityItems.push(item);
                                        else if (item.type === ADVANCE_TYPE) advanceItems.push(item);
                                        else rentItems.push(item);
                                    }

                                    // Group rent items by description + unitPrice
                                    const grouped = [];
                                    for (const item of rentItems) {
                                        const desc = getCleanDescription(item);
                                        const price = Number(item.unitPrice || 0);
                                        const existing = grouped.find(g => g.description === desc && g.unitPrice === price);
                                        if (existing) {
                                            existing.quantity += (item.quantity || 1);
                                            existing.amount += Number(item.amount || 0);
                                        } else {
                                            grouped.push({ description: desc, quantity: item.quantity || 1, unitPrice: price, amount: Number(item.amount || 0) });
                                        }
                                    }

                                    return (
                                        <div className="space-y-2">
                                            {grouped.map((g, idx) => (
                                                <div key={idx} className="flex items-center justify-between rounded-xl bg-cream px-4 py-3">
                                                    <div>
                                                        <div className="text-sm font-medium text-ink">{g.description}</div>
                                                        {g.quantity > 1 && (
                                                            <div className="text-xs text-[#A89C8C]">Qty: {g.quantity} × PKR {formatNumberWithCommas(g.unitPrice)}</div>
                                                        )}
                                                    </div>
                                                    <div className="text-sm font-semibold text-ink">PKR {formatNumberWithCommas(g.amount)}</div>
                                                </div>
                                            ))}

                                            {securityItems.map((item, idx) => (
                                                <div key={`sec-${idx}`} className="flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                                                    <div>
                                                        <div className="text-sm font-medium text-blue-800">Security Deposit</div>
                                                        <div className="text-xs text-blue-500">One-time charge</div>
                                                    </div>
                                                    <div className="text-sm font-semibold text-blue-800">PKR {formatNumberWithCommas(item.amount)}</div>
                                                </div>
                                            ))}

                                            {advanceItems.map((item, idx) => (
                                                <div key={`adv-${idx}`} className="flex items-center justify-between rounded-xl border border-green-100 bg-green-50 px-4 py-3">
                                                    <div>
                                                        <div className="text-sm font-medium text-green-800">Advance Deduction</div>
                                                        <div className="text-xs text-green-500">Applied to invoice</div>
                                                    </div>
                                                    <div className="text-sm font-semibold text-green-700">- PKR {formatNumberWithCommas(Math.abs(item.amount))}</div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Summary */}
                            <div className="flex justify-end border-t border-[#F1E9DC] pt-4">
                                <div className="w-64 space-y-1.5">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-[#6B675F]">Total</span>
                                        <span className="font-semibold text-ink">PKR {formatNumberWithCommas(detailInvoice.totalAmount)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-[#6B675F]">Paid</span>
                                        <span className="font-semibold text-green-600">PKR {formatNumberWithCommas(detailInvoice.paidAmount)}</span>
                                    </div>
                                    <div className="flex justify-between border-t border-[#F1E9DC] pt-1.5 text-sm">
                                        <span className="font-bold text-ink">Balance</span>
                                        <span className="font-bold text-red-600">PKR {formatNumberWithCommas(detailInvoice.balanceAmount)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Payment History */}
                            {detailInvoice.payments?.length > 0 && (
                                <div>
                                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#8C8175]">Payment History</div>
                                    <div className="space-y-2">
                                        {detailInvoice.payments.map((p, idx) => (
                                            <div key={idx} className="flex items-center justify-between rounded-xl bg-cream px-4 py-3">
                                                <div>
                                                    <div className="text-sm text-ink">{moment(p.paymentDate).format('MMM DD, YYYY')}</div>
                                                    <div className="text-xs text-[#8C8175]">
                                                        {convertToPresentableText(p.paymentMethod || 'N/A')}
                                                        {p.markedByCustomer && (
                                                            <span className={`ml-2 ${p.approvedByAdmin ? 'text-green-600' : 'text-amber-600'}`}>
                                                                {p.approvedByAdmin ? '✓ Approved' : '⏳ Pending Approval'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-sm font-semibold text-ink">PKR {formatNumberWithCommas(p.amount)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-end border-t border-[#ECE3D5] px-6 py-4">
                    <button onClick={() => setDetailInvoice(null)} className="rounded-xl px-5 py-2.5 text-sm font-semibold text-[#6B675F] transition-colors hover:bg-cream">
                        Close
                    </button>
                </div>
            </Dialog>

            {/* Mark as Paid Dialog */}
            <Dialog open={!!markPaidDialog} handler={() => setMarkPaidDialog(null)} size="sm" className="rounded-2xl">
                <div className="flex items-center gap-3 border-b border-[#ECE3D5] px-6 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cream-input text-brand">
                        <Wallet size={20} />
                    </div>
                    <h4 className="font-display text-lg font-semibold text-ink">Mark as Paid</h4>
                </div>
                <div className="space-y-4 px-6 py-5">
                    <p className="text-sm text-[#8C8175]">
                        Notify the admin that you&rsquo;ve made a payment. This will require admin approval to be recorded.
                    </p>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[#8C8175]">Amount (PKR)</label>
                        <input
                            type="number"
                            className="h-11 w-full rounded-xl border-[1.5px] border-cream-200 bg-cream px-3.5 text-sm text-[#2B2420] outline-none transition-colors focus:border-brand"
                            value={markPaidForm.amount}
                            onChange={(e) => setMarkPaidForm(prev => ({ ...prev, amount: e.target.value }))}
                            min={1}
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[#8C8175]">Payment Method</label>
                        <select
                            className="h-11 w-full rounded-xl border-[1.5px] border-cream-200 bg-cream px-3 text-sm text-[#2B2420] outline-none transition-colors focus:border-brand"
                            value={markPaidForm.paymentMethod}
                            onChange={(e) => setMarkPaidForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                        >
                            {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                                <option key={key} value={value}>{convertToPresentableText(value)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[#8C8175]">Note (optional)</label>
                        <textarea
                            className="w-full resize-none rounded-xl border-[1.5px] border-cream-200 bg-cream px-3.5 py-2.5 text-sm text-[#2B2420] outline-none transition-colors placeholder:text-[#B7AC9E] focus:border-brand"
                            rows={2}
                            placeholder="E.g. Bank transfer reference number..."
                            value={markPaidForm.note}
                            onChange={(e) => setMarkPaidForm(prev => ({ ...prev, note: e.target.value }))}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2.5 border-t border-[#ECE3D5] px-6 py-4">
                    <button onClick={() => setMarkPaidDialog(null)} className="rounded-xl px-5 py-2.5 text-sm font-semibold text-[#6B675F] transition-colors hover:bg-cream">
                        Cancel
                    </button>
                    <button
                        onClick={handleMarkAsPaid}
                        disabled={markPaidMutation.isLoading}
                        className="flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
                    >
                        {markPaidMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                        Submit
                    </button>
                </div>
            </Dialog>
        </div>
    );
};

export default CustomerInvoicePage;
